import { useState } from "react";
import { Link } from "wouter";
import ScreenHeader from "@/components/mnf/ScreenHeader";
import Icon from "@/components/mnf/Icon";
import { GlassCard, PillTag, Btn, Avatar } from "@/components/mnf/UI";
import {
  useStoreQuery,
  queries,
  mutations,
  type AccountabilityPair,
  type CovenantGoal,
  type CovenantGoalKind,
  type PingKind,
  type ScriptureReactionKind,
} from "@/lib/store";

/**
 * /sisters/partner — the accountability sister hub.
 * Five states unfold here:
 *   1. Empty: no partner yet, show the invite composer
 *   2. Pending sent: waiting on her response
 *   3. Pending received: covenant preview + accept / decline
 *   4. Active: shared dashboard, today's check-in, ping bar
 *   5. Released / completed: gentle close + renew or release with grace
 */
export default function Partner() {
  const me = useStoreQuery(() => queries.currentUser());
  // No fallback to the seeded demo account: every sub-view below WRITES (invite, accept,
  // decline, ping, check-in). See the early return just below.
  const userId = me?.id ?? null;

  const active = useStoreQuery(() => (userId ? queries.activePairForUser(userId) : null));
  const incomingInvites = useStoreQuery(() => (userId ? queries.pendingInvitesForUser(userId) : []));
  const outgoingInvites = useStoreQuery(() => (userId ? queries.pendingInvitesByUser(userId) : []));

  if (!userId) {
    return (
      <div className="px-5 pt-6">
        <p className="mnf-body" style={{ color: "var(--mnf-tg)" }}>
          Sign in to see your accountability sister, sister.
        </p>
      </div>
    );
  }

  return (
    <>
      <ScreenHeader
        variant="forest"
        eyebrow="Walk together"
        title={
          <>
            Accountability <span className="italic">sister</span>
          </>
        }
        subtitle={
          <>
            "Two are better than one, because they have a good return for their labor."
            <span style={{ color: "rgba(229,185,151,0.95)" }}> · Ecclesiastes 4:9</span>
          </>
        }
        trailing={
          <Link href="/sisters">
            <span
              className="inline-flex items-center justify-center"
              style={{
                width: 36,
                height: 36,
                borderRadius: 999,
                background: "rgba(245,240,232,0.14)",
                color: "#F5F0E8",
                cursor: "pointer",
              }}
              aria-label="Back to sisters"
            >
              <Icon name="arrow_back" size={18} />
            </span>
          </Link>
        }
      />

      <div className="px-5 -mt-3 pb-6 space-y-4">
        {active ? (
          <ActivePairView pair={active} userId={userId} />
        ) : incomingInvites.length > 0 ? (
          <IncomingInviteView pair={incomingInvites[0]} userId={userId} />
        ) : outgoingInvites.length > 0 ? (
          <OutgoingInviteView pair={outgoingInvites[0]} userId={userId} />
        ) : (
          <EmptyView userId={userId} />
        )}
      </div>
    </>
  );
}

/* ----------------------------- Active state ------------------------------- */

function ActivePairView({ pair, userId }: { pair: AccountabilityPair; userId: string }) {
  const partner = useStoreQuery(() => queries.partnerProfile(pair, userId));
  const me = useStoreQuery(() => queries.currentUser());
  const today = useStoreQuery(() => queries.todayCheckIn(pair.id));
  const pings = useStoreQuery(() => queries.pingsForPair(pair.id, 6));
  const progress = useStoreQuery(() => queries.pairProgress(pair));
  const history = useStoreQuery(() => queries.checkInsForPair(pair.id));

  const isInviter = pair.inviterId === userId;
  const myAnswer = today ? (isInviter ? today.inviterAnswer : today.inviteeAnswer) : null;
  const partnerAnswer = today ? (isInviter ? today.inviteeAnswer : today.inviterAnswer) : null;

  const [draft, setDraft] = useState(myAnswer ?? "");
  const dayOfSeason = pair.startedAt
    ? Math.max(1, Math.floor((Date.now() - pair.startedAt) / (1000 * 60 * 60 * 24)) + 1)
    : 1;

  function submitDraft() {
    if (!draft.trim()) return;
    mutations.submitCheckIn({ pairId: pair.id, viewerId: userId, answer: draft.trim() });
  }

  function pingPartner(kind: PingKind) {
    mutations.sendPing({ pairId: pair.id, fromUserId: userId, kind });
  }

  function release() {
    if (confirm("Release this season with grace, sister? Your history is kept.")) {
      mutations.releasePair(pair.id);
    }
  }

  return (
    <>
      {/* Side-by-side header card */}
      <GlassCard
        organic
        padding={20}
        style={{
          background: "linear-gradient(135deg, rgba(245,240,232,0.85) 0%, rgba(245,240,232,0.65) 100%)",
        }}
      >
        <div className="flex items-center justify-between" style={{ marginBottom: 14 }}>
          <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)", letterSpacing: "0.18em", fontSize: 10 }}>
            DAY {dayOfSeason} OF {pair.seasonDays}
          </p>
          <PillTag tone="forest">Season is active</PillTag>
        </div>

        <div className="flex items-center justify-between gap-3">
          <SisterTile name={me?.name ?? "You"} initials={initialsOf(me?.name)} streak={me?.streak ?? 0} />
          <div className="flex flex-col items-center" style={{ width: 56 }}>
            <Icon name="favorite" size={20} style={{ color: "var(--mnf-coral, #C0784A)" }} />
            <span
              className="mnf-eyebrow"
              style={{ fontSize: 9, color: "var(--mnf-tg)", marginTop: 2, letterSpacing: "0.14em" }}
            >
              WALKING
            </span>
          </div>
          <SisterTile
            name={partner?.name ?? "Sister"}
            initials={partner?.initials ?? "S"}
            streak={partner?.streak ?? 0}
          />
        </div>

        {/* Progress bar */}
        <div style={{ marginTop: 16 }}>
          <div
            style={{
              height: 6,
              borderRadius: 999,
              background: "rgba(30,74,53,0.10)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${Math.round(progress * 100)}%`,
                height: "100%",
                background: "linear-gradient(90deg, var(--mnf-f) 0%, #C0784A 100%)",
              }}
            />
          </div>
          <div className="flex items-center justify-between" style={{ marginTop: 6 }}>
            <span className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)" }}>
              {Math.max(0, pair.seasonDays - dayOfSeason)} days remaining
            </span>
            <button
              onClick={release}
              className="mnf-body"
              style={{
                fontSize: 11,
                color: "var(--mnf-t)",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              Release with grace
            </button>
          </div>
        </div>
      </GlassCard>

      {/* Today's check-in */}
      <GlassCard organic padding={20}>
        <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)", letterSpacing: "0.16em", fontSize: 10 }}>
          TODAY'S CHECK-IN
        </p>
        <h3
          className="mnf-serif"
          style={{ fontSize: 19, fontWeight: 500, lineHeight: 1.25, marginTop: 6, color: "var(--mnf-fd)" }}
        >
          {today?.prompt ?? "How is your heart this morning, sister?"}
        </h3>

        {/* Your answer */}
        <div style={{ marginTop: 14 }}>
          <p className="mnf-eyebrow" style={{ fontSize: 9, color: "var(--mnf-tg)", letterSpacing: "0.14em" }}>
            YOUR ANSWER
          </p>
          {myAnswer ? (
            <div
              style={{
                padding: 12,
                marginTop: 6,
                borderRadius: 14,
                background: "rgba(30,74,53,0.06)",
                border: "1px solid rgba(30,74,53,0.10)",
              }}
            >
              <p className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-fd)", lineHeight: 1.5 }}>
                {myAnswer}
              </p>
            </div>
          ) : (
            <>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={3}
                placeholder="Speak honestly, sister."
                className="mnf-body w-full"
                style={{
                  marginTop: 6,
                  padding: 12,
                  borderRadius: 14,
                  border: "1px solid rgba(30,74,53,0.18)",
                  background: "rgba(255,255,255,0.7)",
                  fontSize: 13,
                  color: "var(--mnf-fd)",
                  resize: "vertical",
                  outline: "none",
                }}
              />
              <div style={{ marginTop: 8 }}>
                <Btn variant="primary" size="sm" onClick={submitDraft} disabled={!draft.trim()}>
                  Share with your sister
                </Btn>
              </div>
            </>
          )}
        </div>

        {/* Partner's answer */}
        <div style={{ marginTop: 14 }}>
          <p className="mnf-eyebrow" style={{ fontSize: 9, color: "var(--mnf-tg)", letterSpacing: "0.14em" }}>
            {partner?.name?.split(" ")[0]?.toUpperCase() ?? "HER"} ANSWER
          </p>
          <div
            style={{
              padding: 12,
              marginTop: 6,
              borderRadius: 14,
              background: partnerAnswer ? "rgba(192,120,74,0.08)" : "rgba(30,74,53,0.04)",
              border: partnerAnswer ? "1px solid rgba(192,120,74,0.22)" : "1px dashed rgba(30,74,53,0.14)",
            }}
          >
            <p
              className={partnerAnswer ? "mnf-body" : "mnf-body italic"}
              style={{
                fontSize: 13,
                color: partnerAnswer ? "var(--mnf-fd)" : "var(--mnf-tg)",
                lineHeight: 1.5,
              }}
            >
              {partnerAnswer ?? "Not yet, but she'll get there. The day is still hers."}
            </p>
          </div>
        </div>
      </GlassCard>

      {/* Ping bar */}
      <GlassCard organic padding={18}>
        <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)", letterSpacing: "0.16em", fontSize: 10 }}>
          A QUICK NUDGE
        </p>
        <p className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-tg)", marginTop: 4 }}>
          One tap, one whispered word from you to her.
        </p>
        <div className="grid grid-cols-3 gap-2" style={{ marginTop: 12 }}>
          <PingButton kind="praying" onClick={() => pingPartner("praying")} />
          <PingButton kind="won_morning" onClick={() => pingPartner("won_morning")} />
          <PingButton kind="struggling" onClick={() => pingPartner("struggling")} />
        </div>

        {pings.length > 0 && (
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid rgba(30,74,53,0.08)" }}>
            <p className="mnf-eyebrow" style={{ fontSize: 9, color: "var(--mnf-tg)", letterSpacing: "0.14em" }}>
              RECENT WHISPERS
            </p>
            <ul style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
              {pings.map((p) => {
                const fromMe = p.fromUserId === userId;
                const label = pingLabel(p.kind);
                return (
                  <li
                    key={p.id}
                    className="flex items-center gap-2 mnf-body"
                    style={{ fontSize: 12, color: "var(--mnf-tg)" }}
                  >
                    <Icon name={pingIcon(p.kind)} size={14} style={{ color: "var(--mnf-t)" }} />
                    <span style={{ flex: 1 }}>
                      {fromMe ? "You sent" : `${(partner?.name ?? "Sister").split(" ")[0]} sent`} {label}
                    </span>
                    <span style={{ fontSize: 11, opacity: 0.7 }}>{relativeTime(p.sentAt)}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </GlassCard>

      {/* Shared scripture of the week */}
      <SharedScriptureCard pair={pair} userId={userId} partnerName={partner?.name ?? "sister"} />

      {/* Cook together */}
      <CookTogetherCard pair={pair} userId={userId} partnerName={partner?.name ?? "sister"} />

      {/* Covenant */}
      <GlassCard organic padding={20}>
        <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)", letterSpacing: "0.16em", fontSize: 10 }}>
          YOUR COVENANT
        </p>
        <ul style={{ marginTop: 10 }}>
          {pair.covenant.map((g, i) => (
            <li
              key={i}
              className="flex items-start gap-3"
              style={{ padding: "10px 0", borderBottom: i < pair.covenant.length - 1 ? "1px dashed rgba(30,74,53,0.14)" : "none" }}
            >
              <span
                style={{
                  fontSize: 18,
                  marginTop: 1,
                }}
              >
                {covenantIcon(g.kind)}
              </span>
              <div style={{ flex: 1 }}>
                <p className="mnf-eyebrow" style={{ fontSize: 9, color: "var(--mnf-t)", letterSpacing: "0.14em" }}>
                  {g.kind.toUpperCase()}
                </p>
                <p className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-fd)", marginTop: 2, lineHeight: 1.4 }}>
                  {g.text}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </GlassCard>

      {/* History */}
      {history.length > 1 && (
        <GlassCard organic padding={20}>
          <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)", letterSpacing: "0.16em", fontSize: 10 }}>
            YOUR SHARED DAYS
          </p>
          <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 12 }}>
            {history.slice(1, 5).map((c) => {
              const mine = isInviter ? c.inviterAnswer : c.inviteeAnswer;
              const hers = isInviter ? c.inviteeAnswer : c.inviterAnswer;
              return (
                <div key={c.id}>
                  <p className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)" }}>
                    {formatDate(c.date)} · {c.prompt}
                  </p>
                  <div className="grid grid-cols-2 gap-2" style={{ marginTop: 6 }}>
                    <HistoryBubble label="You" text={mine} />
                    <HistoryBubble label={partner?.name?.split(" ")[0] ?? "Her"} text={hers} accent />
                  </div>
                </div>
              );
            })}
          </div>
        </GlassCard>
      )}

      {/* Direct message link */}
      <Link href="/messages">
        <Btn variant="secondary" size="md" style={{ width: "100%" }}>
          <Icon name="chat_bubble" size={16} />
          <span style={{ marginLeft: 6 }}>Send {partner?.name?.split(" ")[0] ?? "her"} a longer note</span>
        </Btn>
      </Link>
    </>
  );
}

function SisterTile({ name, initials, streak }: { name: string; initials: string; streak: number }) {
  return (
    <div className="flex flex-col items-center text-center" style={{ flex: 1 }}>
      <Avatar initials={initials} size={52} />
      <p
        className="mnf-serif"
        style={{ fontSize: 14, fontWeight: 500, color: "var(--mnf-fd)", marginTop: 8, lineHeight: 1.2 }}
      >
        {name.split(" ")[0]}
      </p>
      <p className="mnf-eyebrow" style={{ fontSize: 9, color: "var(--mnf-t)", marginTop: 2, letterSpacing: "0.14em" }}>
        {streak}-DAY STREAK
      </p>
    </div>
  );
}

function HistoryBubble({ label, text, accent = false }: { label: string; text: string | null; accent?: boolean }) {
  return (
    <div
      style={{
        padding: 10,
        borderRadius: 12,
        background: accent ? "rgba(192,120,74,0.08)" : "rgba(30,74,53,0.06)",
        border: accent ? "1px solid rgba(192,120,74,0.18)" : "1px solid rgba(30,74,53,0.10)",
        minHeight: 56,
      }}
    >
      <p className="mnf-eyebrow" style={{ fontSize: 8.5, color: "var(--mnf-tg)", letterSpacing: "0.14em" }}>
        {label.toUpperCase()}
      </p>
      <p
        className="mnf-body"
        style={{ fontSize: 11.5, color: text ? "var(--mnf-fd)" : "var(--mnf-tg)", marginTop: 4, lineHeight: 1.45 }}
      >
        {text ?? "Quiet that day."}
      </p>
    </div>
  );
}

function PingButton({ kind, onClick }: { kind: PingKind; onClick: () => void }) {
  const meta = pingMeta(kind);
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center justify-center"
      style={{
        padding: "12px 6px",
        borderRadius: 18,
        border: "1px solid rgba(30,74,53,0.12)",
        background: meta.bg,
        cursor: "pointer",
        transition: "transform 0.15s ease",
      }}
    >
      <span style={{ fontSize: 22 }}>{meta.emoji}</span>
      <span
        className="mnf-body"
        style={{ fontSize: 11, color: meta.color, marginTop: 4, fontWeight: 600, textAlign: "center", lineHeight: 1.2 }}
      >
        {meta.label}
      </span>
    </button>
  );
}

/* ----------------------------- Incoming invite ---------------------------- */

function IncomingInviteView({ pair, userId }: { pair: AccountabilityPair; userId: string }) {
  const inviter = useStoreQuery(() => queries.partnerProfile(pair, userId));
  return (
    <GlassCard
      organic
      padding={22}
      style={{
        background: "linear-gradient(165deg, rgba(192,120,74,0.10) 0%, rgba(245,240,232,0.85) 100%)",
      }}
    >
      <PillTag tone="coral">An invitation, sister</PillTag>
      <h3
        className="mnf-serif"
        style={{ fontSize: 22, fontWeight: 500, lineHeight: 1.2, marginTop: 12, color: "var(--mnf-fd)" }}
      >
        {inviter?.name?.split(" ")[0] ?? "A sister"} is asking to walk with you.
      </h3>
      <p className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-tg)", marginTop: 8, lineHeight: 1.55 }}>
        For {pair.seasonDays} days, with these three holds:
      </p>

      <ul style={{ marginTop: 12, marginBottom: 16 }}>
        {pair.covenant.map((g, i) => (
          <li
            key={i}
            className="flex items-start gap-2"
            style={{ padding: "8px 0", borderBottom: i < pair.covenant.length - 1 ? "1px dashed rgba(30,74,53,0.14)" : "none" }}
          >
            <span style={{ fontSize: 16 }}>{covenantIcon(g.kind)}</span>
            <span className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-fd)" }}>
              {g.text}
            </span>
          </li>
        ))}
      </ul>

      <div className="flex items-center gap-2">
        <Btn
          variant="primary"
          size="md"
          style={{ flex: 1 }}
          onClick={() => mutations.acceptPair(pair.id)}
        >
          Yes, I will walk with her
        </Btn>
        <Btn variant="ghost" size="md" onClick={() => mutations.declinePair(pair.id)}>
          Not this season
        </Btn>
      </div>
    </GlassCard>
  );
}

/* ----------------------------- Outgoing invite ---------------------------- */

function OutgoingInviteView({ pair, userId }: { pair: AccountabilityPair; userId: string }) {
  const invitee = useStoreQuery(() => queries.partnerProfile(pair, userId));
  return (
    <GlassCard organic padding={22}>
      <PillTag tone="fiber">Waiting on her heart</PillTag>
      <h3
        className="mnf-serif"
        style={{ fontSize: 22, fontWeight: 500, lineHeight: 1.2, marginTop: 12, color: "var(--mnf-fd)" }}
      >
        Your invite is with {invitee?.name?.split(" ")[0] ?? "her"}.
      </h3>
      <p className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-tg)", marginTop: 8, lineHeight: 1.55 }}>
        She'll see your covenant and decide in her own time. Pray over it, sister.
      </p>
      <div style={{ marginTop: 14 }}>
        <Btn variant="ghost" size="sm" onClick={() => mutations.declinePair(pair.id)}>
          Withdraw invite
        </Btn>
      </div>
    </GlassCard>
  );
}

/* ------------------------------- Empty state ------------------------------ */

const STARTER_GOALS: Array<{ kind: CovenantGoalKind; emoji: string; text: string }> = [
  { kind: "movement",    emoji: "🌿", text: "Move 4 days a week" },
  { kind: "nourishment", emoji: "🍇", text: "Three honest meals a day" },
  { kind: "scripture",   emoji: "📖", text: "Open the Word every morning" },
  { kind: "rest",        emoji: "🌙", text: "Phone down by 9pm" },
];

function EmptyView({ userId }: { userId: string }) {
  const candidates = useStoreQuery(() => queries.availableSistersToPair(userId));
  const [selected, setSelected] = useState<string | null>(null);
  const [season, setSeason] = useState<30 | 60 | 90>(30);
  const [goals, setGoals] = useState<CovenantGoal[]>([
    { kind: "movement",  text: "Move 4 days a week" },
    { kind: "scripture", text: "Open the Word every morning" },
  ]);

  function toggleStarter(s: typeof STARTER_GOALS[number]) {
    const exists = goals.some((g) => g.text === s.text);
    if (exists) {
      setGoals(goals.filter((g) => g.text !== s.text));
    } else if (goals.length < 3) {
      setGoals([...goals, { kind: s.kind, text: s.text }]);
    }
  }

  function send() {
    if (!selected || goals.length === 0) return;
    mutations.invitePartner({
      inviterId: userId,
      inviteeId: selected,
      seasonDays: season,
      covenant: goals,
    });
  }

  return (
    <>
      <MatchSuggestionCard userId={userId} />
      <GlassCard organic padding={22}>
        <PillTag tone="forest">A sister to walk with</PillTag>
        <h3
          className="mnf-serif"
          style={{ fontSize: 22, fontWeight: 500, lineHeight: 1.2, marginTop: 12, color: "var(--mnf-fd)" }}
        >
          Choose one sister.
        </h3>
        <p className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-tg)", marginTop: 8, lineHeight: 1.55 }}>
          Accountability is one heart at a time, sister. You'll send her a covenant and she'll say yes or wait.
        </p>

        <ul style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
          {candidates.length === 0 && (
            <li className="mnf-body italic" style={{ fontSize: 12, color: "var(--mnf-tg)" }}>
              No sisters available right now. Invite one to the garden first.
            </li>
          )}
          {candidates.map((c) => (
            <li key={c.id}>
              <button
                onClick={() => setSelected(c.id === selected ? null : c.id)}
                className="flex items-center gap-3 w-full"
                style={{
                  padding: 12,
                  borderRadius: 16,
                  border: c.id === selected ? "2px solid var(--mnf-f)" : "1px solid rgba(30,74,53,0.14)",
                  background: c.id === selected ? "rgba(30,74,53,0.06)" : "rgba(255,255,255,0.6)",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <Avatar initials={c.initials} size={40} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p className="mnf-serif" style={{ fontSize: 15, fontWeight: 500, color: "var(--mnf-fd)" }}>
                    {c.name}
                  </p>
                  <p className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)", marginTop: 1 }}>
                    {c.tier} · {c.streak}-day streak
                  </p>
                </div>
                {c.id === selected && <Icon name="check_circle" size={20} style={{ color: "var(--mnf-f)" }} />}
              </button>
            </li>
          ))}
        </ul>
      </GlassCard>

      {/* Season length */}
      <GlassCard organic padding={20}>
        <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)", letterSpacing: "0.16em", fontSize: 10 }}>
          A SEASON
        </p>
        <h4
          className="mnf-serif"
          style={{ fontSize: 17, fontWeight: 500, color: "var(--mnf-fd)", marginTop: 4, lineHeight: 1.3 }}
        >
          How long will you walk together?
        </h4>
        <div className="grid grid-cols-3 gap-2" style={{ marginTop: 12 }}>
          {([30, 60, 90] as const).map((d) => (
            <button
              key={d}
              onClick={() => setSeason(d)}
              className="mnf-serif"
              style={{
                padding: "12px 8px",
                borderRadius: 16,
                border: season === d ? "2px solid var(--mnf-f)" : "1px solid rgba(30,74,53,0.14)",
                background: season === d ? "var(--mnf-f)" : "rgba(255,255,255,0.6)",
                color: season === d ? "#F5F0E8" : "var(--mnf-fd)",
                fontWeight: 500,
                fontSize: 16,
                cursor: "pointer",
              }}
            >
              {d}
              <span className="mnf-body" style={{ fontSize: 11, marginLeft: 3, opacity: 0.85 }}>
                days
              </span>
            </button>
          ))}
        </div>
      </GlassCard>

      {/* Covenant goals */}
      <GlassCard organic padding={20}>
        <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)", letterSpacing: "0.16em", fontSize: 10 }}>
          YOUR COVENANT
        </p>
        <h4
          className="mnf-serif"
          style={{ fontSize: 17, fontWeight: 500, color: "var(--mnf-fd)", marginTop: 4, lineHeight: 1.3 }}
        >
          Choose 1 to 3 holds.
        </h4>
        <p className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-tg)", marginTop: 4 }}>
          {goals.length} of 3 chosen
        </p>

        <ul style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          {STARTER_GOALS.map((s) => {
            const on = goals.some((g) => g.text === s.text);
            const disabled = !on && goals.length >= 3;
            return (
              <li key={s.text}>
                <button
                  onClick={() => toggleStarter(s)}
                  disabled={disabled}
                  className="flex items-center gap-3 w-full"
                  style={{
                    padding: 12,
                    borderRadius: 14,
                    border: on ? "2px solid var(--mnf-f)" : "1px solid rgba(30,74,53,0.14)",
                    background: on ? "rgba(30,74,53,0.06)" : "rgba(255,255,255,0.6)",
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.5 : 1,
                    textAlign: "left",
                  }}
                >
                  <span style={{ fontSize: 20 }}>{s.emoji}</span>
                  <span className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-fd)", flex: 1 }}>
                    {s.text}
                  </span>
                  {on && <Icon name="check_circle" size={18} style={{ color: "var(--mnf-f)" }} />}
                </button>
              </li>
            );
          })}
        </ul>
      </GlassCard>

      {/* Send */}
      <Btn
        variant="primary"
        size="lg"
        style={{ width: "100%" }}
        onClick={send}
        disabled={!selected || goals.length === 0}
      >
        Send the covenant
      </Btn>
    </>
  );
}

/* --------------------------------- Helpers -------------------------------- */

function initialsOf(name: string | undefined | null): string {
  if (!name) return "Y";
  return name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
}

function covenantIcon(kind: CovenantGoalKind): string {
  return { movement: "🌿", nourishment: "🍇", scripture: "📖", rest: "🌙", custom: "✨" }[kind];
}

function pingMeta(kind: PingKind): { emoji: string; label: string; bg: string; color: string } {
  if (kind === "praying") {
    return { emoji: "🙏", label: "Praying for you", bg: "rgba(127,119,221,0.10)", color: "var(--mnf-purple, #7F77DD)" };
  }
  if (kind === "won_morning") {
    return { emoji: "🌅", label: "I won the morning", bg: "rgba(160,120,32,0.10)", color: "var(--mnf-gold, #A07820)" };
  }
  return { emoji: "🤍", label: "I'm struggling", bg: "rgba(192,120,74,0.10)", color: "var(--mnf-coral, #C0784A)" };
}

function pingLabel(kind: PingKind): string {
  return pingMeta(kind).label.toLowerCase();
}

function pingIcon(kind: PingKind): string {
  if (kind === "praying") return "favorite";
  if (kind === "won_morning") return "wb_sunny";
  return "healing";
}

function relativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d === 1) return "yesterday";
  return `${d}d ago`;
}

function formatDate(ymd: string): string {
  const [y, m, d] = ymd.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/* --------------------------- Shared scripture card ------------------------ */

function SharedScriptureCard({
  pair,
  userId,
  partnerName,
}: {
  pair: AccountabilityPair;
  userId: string;
  partnerName: string;
}) {
  const scripture = useStoreQuery(() => queries.pairScriptureOfWeek(pair.id));
  if (!scripture) return null;

  const isInviter = pair.inviterId === userId;
  const myReaction = isInviter ? scripture.inviterReaction : scripture.inviteeReaction;
  const herReaction = isInviter ? scripture.inviteeReaction : scripture.inviterReaction;
  const herNote = isInviter ? scripture.inviteeNote : scripture.inviterNote;
  const firstName = partnerName.split(" ")[0] ?? "she";

  function react(kind: ScriptureReactionKind) {
    mutations.reactToScripture({ pairId: pair.id, viewerId: userId, reaction: kind });
  }

  return (
    <GlassCard
      organic
      padding={20}
      style={{
        background: "linear-gradient(135deg, rgba(127,119,221,0.10) 0%, rgba(245,240,232,0.85) 100%)",
        border: "1px solid rgba(127,119,221,0.22)",
      }}
    >
      <div className="flex items-center justify-between" style={{ marginBottom: 10 }}>
        <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)", letterSpacing: "0.16em", fontSize: 10 }}>
          THIS WEEK'S VERSE
        </p>
        <Icon name="menu_book" size={16} style={{ color: "var(--mnf-purple, #7F77DD)" }} />
      </div>
      <p
        className="mnf-serif"
        style={{ fontSize: 17, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.4 }}
      >
        "{scripture.text}"
      </p>
      <p
        className="mnf-eyebrow"
        style={{ fontSize: 10, color: "var(--mnf-t)", letterSpacing: "0.14em", marginTop: 8 }}
      >
        {scripture.reference.toUpperCase()}
      </p>

      {herReaction && (
        <div
          style={{
            marginTop: 14,
            padding: "10px 12px",
            borderRadius: 12,
            background: "rgba(255,255,255,0.55)",
            border: "1px solid rgba(127,119,221,0.18)",
          }}
        >
          <p className="mnf-body" style={{ fontSize: 11.5, color: "var(--mnf-fd)", lineHeight: 1.45 }}>
            <span style={{ marginRight: 6 }}>{reactionEmoji(herReaction)}</span>
            {firstName} {reactionVerb(herReaction)}
            {herNote ? <span style={{ fontStyle: "italic" }}>, "{herNote}"</span> : null}
          </p>
        </div>
      )}

      <div className="flex items-center gap-2" style={{ marginTop: 14 }}>
        <ReactionButton kind="heart" active={myReaction === "heart"} onClick={() => react("heart")} />
        <ReactionButton kind="amen" active={myReaction === "amen"} onClick={() => react("amen")} />
        <ReactionButton kind="praying" active={myReaction === "praying"} onClick={() => react("praying")} />
        {myReaction && (
          <span
            className="mnf-eyebrow"
            style={{ fontSize: 10, color: "var(--mnf-tg)", letterSpacing: "0.14em", marginLeft: "auto" }}
          >
            SHARED
          </span>
        )}
      </div>
    </GlassCard>
  );
}

function ReactionButton({
  kind,
  active,
  onClick,
}: {
  kind: ScriptureReactionKind;
  active: boolean;
  onClick: () => void;
}) {
  const label = kind === "heart" ? "Heart" : kind === "amen" ? "Amen" : "Praying";
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1"
      style={{
        padding: "7px 12px",
        borderRadius: 999,
        border: active ? "1.5px solid var(--mnf-purple, #7F77DD)" : "1px solid rgba(30,74,53,0.16)",
        background: active ? "rgba(127,119,221,0.14)" : "rgba(255,255,255,0.7)",
        color: active ? "var(--mnf-purple, #7F77DD)" : "var(--mnf-fd)",
        fontSize: 12,
        cursor: "pointer",
      }}
    >
      <span>{reactionEmoji(kind)}</span>
      <span className="mnf-body">{label}</span>
    </button>
  );
}

function reactionEmoji(kind: ScriptureReactionKind): string {
  if (kind === "heart") return "💛";
  if (kind === "amen") return "🕊️";
  return "🙏";
}

function reactionVerb(kind: ScriptureReactionKind): string {
  if (kind === "heart") return "loved this";
  if (kind === "amen") return "said amen";
  return "is praying with you";
}

/* ----------------------------- Cook together ------------------------------ */

function CookTogetherCard({
  pair,
  userId,
  partnerName,
}: {
  pair: AccountabilityPair;
  userId: string;
  partnerName: string;
}) {
  const sr = useStoreQuery(() => queries.pairSharedRecipeOfWeek(pair.id));
  const recipe = useStoreQuery(() => (sr ? queries.recipeById(sr.recipeId) : null));
  const candidates = useStoreQuery(() => queries.trendingRecipes(undefined, 4));
  const firstName = partnerName.split(" ")[0] ?? "she";

  if (!sr) {
    return (
      <GlassCard organic padding={20}>
        <div className="flex items-center justify-between" style={{ marginBottom: 10 }}>
          <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)", letterSpacing: "0.16em", fontSize: 10 }}>
            COOK TOGETHER
          </p>
          <Icon name="restaurant" size={16} style={{ color: "var(--mnf-coral, #C0784A)" }} />
        </div>
        <p className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-tg)", lineHeight: 1.5 }}>
          Pick one recipe to cook this week. You'll both feel her hand on the pan, sister.
        </p>
        <ul style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          {candidates.slice(0, 3).map((r) => (
            <li key={r.id}>
              <button
                onClick={() =>
                  mutations.pickSharedRecipe({ pairId: pair.id, viewerId: userId, recipeId: r.id })
                }
                className="flex items-center gap-3 w-full"
                style={{
                  padding: 10,
                  borderRadius: 14,
                  border: "1px solid rgba(30,74,53,0.14)",
                  background: "rgba(255,255,255,0.7)",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <span style={{ fontSize: 22 }}>{r.heroEmojis[0] ?? "🍲"}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p className="mnf-serif" style={{ fontSize: 14, fontWeight: 500, color: "var(--mnf-fd)" }}>
                    {r.title}
                  </p>
                  <p className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)", marginTop: 1 }}>
                    {r.durationMin} min
                  </p>
                </div>
                <Icon name="add" size={16} style={{ color: "var(--mnf-coral, #C0784A)" }} />
              </button>
            </li>
          ))}
        </ul>
      </GlassCard>
    );
  }

  const isInviter = pair.inviterId === userId;
  const myCooked = isInviter ? sr.inviterCookedAt : sr.inviteeCookedAt;
  const herCooked = isInviter ? sr.inviteeCookedAt : sr.inviterCookedAt;
  const pickedByMe = sr.pickedBy === userId;

  return (
    <GlassCard
      organic
      padding={20}
      style={{
        background: "linear-gradient(135deg, rgba(192,120,74,0.10) 0%, rgba(245,240,232,0.85) 100%)",
        border: "1px solid rgba(192,120,74,0.22)",
      }}
    >
      <div className="flex items-center justify-between" style={{ marginBottom: 10 }}>
        <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)", letterSpacing: "0.16em", fontSize: 10 }}>
          COOK TOGETHER THIS WEEK
        </p>
        <Icon name="restaurant" size={16} style={{ color: "var(--mnf-coral, #C0784A)" }} />
      </div>

      <Link href={`/nourish/${sr.recipeId}`}>
        <div
          className="flex items-center gap-3"
          style={{
            padding: 12,
            borderRadius: 14,
            background: "rgba(255,255,255,0.6)",
            border: "1px solid rgba(192,120,74,0.18)",
            cursor: "pointer",
          }}
        >
          <span style={{ fontSize: 28 }}>{recipe?.heroEmojis[0] ?? "🍲"}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p
              className="mnf-serif"
              style={{ fontSize: 15, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.25 }}
            >
              {recipe?.title ?? "Shared recipe"}
            </p>
            <p className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)", marginTop: 2 }}>
              {pickedByMe ? "You picked this" : `${firstName} picked this`}
              {recipe ? ` · ${recipe.durationMin} min` : null}
            </p>
          </div>
          <Icon name="chevron_right" size={18} style={{ color: "var(--mnf-tg)" }} />
        </div>
      </Link>

      <div className="grid grid-cols-2 gap-2" style={{ marginTop: 12 }}>
        <CookStatusTile
          label="You"
          cookedAt={myCooked}
          onMark={() =>
            mutations.markCookedTogether({ pairId: pair.id, viewerId: userId })
          }
          isSelf
        />
        <CookStatusTile label={firstName} cookedAt={herCooked} />
      </div>
    </GlassCard>
  );
}

function CookStatusTile({
  label,
  cookedAt,
  onMark,
  isSelf,
}: {
  label: string;
  cookedAt: number | null;
  onMark?: () => void;
  isSelf?: boolean;
}) {
  const cooked = !!cookedAt;
  const interactive = isSelf && !cooked && !!onMark;
  return (
    <button
      onClick={interactive ? onMark : undefined}
      disabled={!interactive}
      style={{
        padding: 10,
        borderRadius: 12,
        border: cooked ? "1.5px solid var(--mnf-f)" : "1px solid rgba(30,74,53,0.16)",
        background: cooked ? "rgba(30,74,53,0.08)" : "rgba(255,255,255,0.6)",
        cursor: interactive ? "pointer" : "default",
        textAlign: "left",
      }}
    >
      <p className="mnf-eyebrow" style={{ fontSize: 9, color: "var(--mnf-t)", letterSpacing: "0.14em" }}>
        {label.toUpperCase()}
      </p>
      <p
        className="mnf-body"
        style={{ fontSize: 12, color: "var(--mnf-fd)", marginTop: 4, display: "flex", alignItems: "center", gap: 6 }}
      >
        {cooked ? (
          <>
            <Icon name="check_circle" size={14} style={{ color: "var(--mnf-f)" }} />
            <span>Cooked</span>
          </>
        ) : isSelf ? (
          <>
            <Icon name="add" size={14} style={{ color: "var(--mnf-coral, #C0784A)" }} />
            <span>I cooked this</span>
          </>
        ) : (
          <>
            <Icon name="hourglass_empty" size={14} style={{ color: "var(--mnf-tg)" }} />
            <span>Not yet</span>
          </>
        )}
      </p>
    </button>
  );
}

/* --------------------------- Match suggestion card ------------------------ */

function MatchSuggestionCard({ userId }: { userId: string }) {
  const suggestion = useStoreQuery(() => queries.matchSuggestionForUser(userId));
  const profile = useStoreQuery(() =>
    suggestion ? queries.sisterProfile(suggestion.candidateId) : null,
  );
  if (!suggestion || !profile) return null;

  function accept() {
    if (!suggestion || !profile) return;
    mutations.acceptMatchSuggestion({
      suggestionId: suggestion.id,
      viewerId: userId,
      candidateId: suggestion.candidateId,
    });
  }

  function skip() {
    if (!suggestion || !profile) return;
    mutations.skipMatchSuggestion({
      suggestionId: suggestion.id,
      viewerId: userId,
      candidateId: suggestion.candidateId,
    });
  }

  return (
    <GlassCard
      organic
      padding={20}
      style={{
        background: "linear-gradient(135deg, rgba(168,212,184,0.18) 0%, rgba(245,240,232,0.85) 100%)",
        border: "1px solid rgba(30,74,53,0.18)",
      }}
    >
      <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
        <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)", letterSpacing: "0.16em", fontSize: 10 }}>
          A SISTER FOR YOUR SEASON
        </p>
        <Icon name="auto_awesome" size={16} style={{ color: "var(--mnf-f)" }} />
      </div>

      <div className="flex items-center gap-3">
        <Avatar initials={profile.initials} size={52} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3
            className="mnf-serif"
            style={{ fontSize: 17, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.2 }}
          >
            {profile.name}
          </h3>
          <p className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-tg)", marginTop: 2 }}>
            {profile.tier ? `${profile.tier}` : "Sister"}
            {profile.streak ? ` · ${profile.streak}-day streak` : null}
          </p>
        </div>
      </div>

      <p
        className="mnf-body"
        style={{ fontSize: 13, color: "var(--mnf-fd)", marginTop: 12, lineHeight: 1.5, fontStyle: "italic" }}
      >
        {suggestion.reason}
      </p>

      <div className="flex items-center gap-2" style={{ marginTop: 14 }}>
        <Btn variant="primary" size="md" onClick={accept} style={{ flex: 1 }}>
          <Icon name="favorite" size={14} />
          <span style={{ marginLeft: 6 }}>Walk with her</span>
        </Btn>
        <Btn variant="secondary" size="md" onClick={skip} style={{ flex: 1 }}>
          Not this season
        </Btn>
      </div>
    </GlassCard>
  );
}
